import React, { useState, useEffect } from 'react';
import { SiteResolverService, SiteResolutionResult } from '../../services/siteResolverService';
import { PublishedSiteSnapshot, YachtItem, BlogPost, YachtReservation } from '../../types';
import { SectionRenderer } from '../editor/SectionRenderer';
import { PlatformAttribution } from '../common/PlatformAttribution';
import { PublicYachtDetailModal } from '../yachts/PublicYachtDetailModal';
import { WebsiteThemeProvider } from '../../theme/WebsiteThemeProvider';
import { realtimeDbService as dbService } from '../../services/realtimeDbService';
import { checkSubmissionRateLimit, escapeHtml, sanitizeUrl } from '../../utils/security';
import { SeoEngine } from '../../services/seoEngine';
import { Helmet } from 'react-helmet-async';
import confetti from 'canvas-confetti';
import {
  X,
  Globe,
  ShieldCheck,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Users,
  Anchor,
  CheckCircle2,
  Send,
  Sparkles,
  AlertTriangle,
  Loader2,
  Lock,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';

interface StandalonePublicWebsiteEngineProps {
  initialTarget?: string;
  onExitPreview?: () => void;
}

export const StandalonePublicWebsiteEngine: React.FC<StandalonePublicWebsiteEngineProps> = React.memo(({
  initialTarget,
  onExitPreview,
}) => {
  const [resolution, setResolution] = useState<SiteResolutionResult | null>(() => {
    if (typeof window === 'undefined') return null;
    const urlParams = new URLSearchParams(window.location.search);
    const target = initialTarget || urlParams.get('site') || urlParams.get('domain') || window.location.hostname;
    return SiteResolverService.getCachedSite(target);
  });
  const [isLoading, setIsLoading] = useState(() => !resolution);
  const [currentPath, setCurrentPath] = useState<string>(
    typeof window !== 'undefined' ? window.location.pathname || '/' : '/'
  );
  const [selectedYacht, setSelectedYacht] = useState<YachtItem | null>(null);
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [targetYachtForReservation, setTargetYachtForReservation] = useState<YachtItem | undefined>(undefined);
  const [selectedBlogPost, setSelectedBlogPost] = useState<BlogPost | null>(null);

  // Reservation form state
  const [resName, setResName] = useState('');
  const [resEmail, setResEmail] = useState('');
  const [resPhone, setResPhone] = useState('');
  const [resStart, setResStart] = useState('2026-06-15');
  const [resEnd, setResEnd] = useState('2026-06-22');
  const [resGuests, setResGuests] = useState(6);
  const [resNotes, setResNotes] = useState('');
  const [resSubmitted, setResSubmitted] = useState(false);
  const [honeypotValue, setHoneypotValue] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmittingRes, setIsSubmittingRes] = useState(false);

  useEffect(() => {
    let isMounted = true;
    if (!resolution) {
      setIsLoading(true);
    }

    // Determine target from prop, query parameter, or hostname
    const urlParams = new URLSearchParams(window.location.search);
    const target = initialTarget || urlParams.get('site') || urlParams.get('domain') || window.location.hostname;

    SiteResolverService.resolveSite(target).then((res) => {
      if (isMounted) {
        setResolution(res);
        setIsLoading(false);

        if (res.redirectUrl && !onExitPreview) {
          const currentHost = window.location.hostname.toLowerCase();
          const isPlatformHost =
            currentHost.includes('adiosoft.com') ||
            currentHost.includes('localhost') ||
            currentHost.includes('127.0.0.1') ||
            currentHost.includes('run.app');
          if (!isPlatformHost) {
            window.location.replace(res.redirectUrl);
            return;
          }
        }

        if (res.success && res.snapshot?.siteId) {
          dbService.incrementSiteViewCount(res.snapshot.siteId).catch((err) => console.warn('View count error:', err));
        }
      }
    });

    return () => {
      isMounted = false;
    };
  }, [initialTarget]);

  // Handle URL history popstate (browser back/forward navigation)
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname || '/');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Update SEO, Canonical link, OpenGraph, Twitter and JSON-LD structured data on route/page change
  useEffect(() => {
    if (!resolution || !resolution.success || !resolution.snapshot) return;
    const snapshot = resolution.snapshot;
    const pages = snapshot.pages || [];
    const activePage =
      pages.find((p) => p && (p.slug === currentPath || `/${p.slug}` === currentPath)) ||
      pages.find((p) => p && p.isHomePage) ||
      pages[0];

    const primaryDomain =
      snapshot.domains?.find((d) => d.isPrimary)?.domain ||
      resolution.normalizedDomain ||
      window.location.hostname;
    const canonicalBase = primaryDomain.startsWith('http') ? primaryDomain : `https://${primaryDomain}`;
    const cleanPath = currentPath === '/' ? '' : currentPath;
    const canonicalUrl = `${canonicalBase}${cleanPath}`;

    SeoEngine.updateHead({
      siteName: snapshot.name,
      pageTitle: activePage?.title,
      tagline: (snapshot.settings as any)?.tagline,
      description: activePage?.seoDescription || snapshot.seo?.description || (snapshot.settings as any)?.description,
      canonicalUrl,
      imageUrl: snapshot.theme?.logoUrl,
      industry: snapshot.industry,
      phone: snapshot.settings?.phone,
      email: snapshot.settings?.email,
      address: snapshot.settings?.address,
      yachts: snapshot.yachts || [],
      blogPosts: snapshot.blogPosts || [],
    });

    // Check for direct yacht route /yatlar/{slug}
    if (currentPath.startsWith('/yatlar/') && currentPath.length > 8) {
      const yachtSlug = currentPath.replace('/yatlar/', '').trim();
      const matchedYacht = (snapshot.yachts || []).find(
        (y) => y && !y.isDeleted && (y.slug === yachtSlug || y.id === yachtSlug)
      );
      if (matchedYacht) {
        setSelectedYacht(matchedYacht);
      }
    }
  }, [resolution, currentPath]);

  // Handle sitemap.xml client-side preview
  if (currentPath === '/sitemap.xml' && resolution?.snapshot) {
    const primaryDomain =
      resolution.snapshot.domains?.find((d) => d.isPrimary)?.domain ||
      resolution.normalizedDomain ||
      window.location.hostname;
    const sitemapXml = SeoEngine.generateSitemapXml(resolution.snapshot, primaryDomain);
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 p-8 font-mono text-xs overflow-auto">
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="flex items-center justify-between pb-4 border-b border-slate-700">
            <span className="text-emerald-400 font-bold">XML SITEMAP ({primaryDomain}/sitemap.xml)</span>
            <button
              onClick={() => {
                setCurrentPath('/');
                window.history.pushState({}, '', '/');
              }}
              className="px-3 py-1 bg-slate-800 hover:bg-slate-700 rounded text-white text-xs"
            >
              Ana Sayfaya Dön
            </button>
          </div>
          <pre className="whitespace-pre-wrap leading-relaxed text-slate-300">{sitemapXml}</pre>
        </div>
      </div>
    );
  }

  // Handle robots.txt client-side preview
  if (currentPath === '/robots.txt' && resolution?.snapshot) {
    const primaryDomain =
      resolution.snapshot.domains?.find((d) => d.isPrimary)?.domain ||
      resolution.normalizedDomain ||
      window.location.hostname;
    const robotsTxt = SeoEngine.generateRobotsTxt(primaryDomain, true);
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 p-8 font-mono text-xs overflow-auto">
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="flex items-center justify-between pb-4 border-b border-slate-700">
            <span className="text-emerald-400 font-bold">ROBOTS.TXT ({primaryDomain}/robots.txt)</span>
            <button
              onClick={() => {
                setCurrentPath('/');
                window.history.pushState({}, '', '/');
              }}
              className="px-3 py-1 bg-slate-800 hover:bg-slate-700 rounded text-white text-xs"
            >
              Ana Sayfaya Dön
            </button>
          </div>
          <pre className="whitespace-pre-wrap leading-relaxed text-slate-300">{robotsTxt}</pre>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex flex-col animate-pulse selection:bg-slate-200">
        {/* Progressive minimal navbar placeholder without blocking message */}
        <div className="h-18 border-b border-slate-100 px-4 sm:px-8 flex items-center justify-between">
          <div className="w-32 h-7 bg-slate-200 rounded-lg" />
          <div className="hidden md:flex items-center gap-6">
            <div className="w-16 h-4 bg-slate-100 rounded" />
            <div className="w-16 h-4 bg-slate-100 rounded" />
            <div className="w-16 h-4 bg-slate-100 rounded" />
          </div>
          <div className="w-28 h-9 bg-slate-200 rounded-xl" />
        </div>
        {/* Progressive hero placeholder */}
        <div className="max-w-5xl mx-auto px-4 sm:px-8 pt-16 pb-20 w-full flex flex-col items-center text-center space-y-4">
          <div className="w-48 h-6 bg-slate-100 rounded-full" />
          <div className="w-3/4 max-w-lg h-10 bg-slate-200 rounded-xl mt-2" />
          <div className="w-1/2 max-w-sm h-5 bg-slate-100 rounded-lg" />
          <div className="w-36 h-11 bg-slate-200 rounded-xl mt-4" />
        </div>
      </div>
    );
  }

  // Handle Error / Suspended / Expired / Not Found states
  if (!resolution || !resolution.success || !resolution.snapshot) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-100 p-6 text-center select-none">
        <div className="w-16 h-16 rounded-3xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center mb-4">
          <AlertTriangle className="w-8 h-8 text-rose-400" />
        </div>

        <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mb-2">
          {resolution?.status === 'SUSPENDED'
            ? 'Web Sitesi Askıya Alındı'
            : resolution?.status === 'EXPIRED'
            ? 'Yayın Süresi Sona Erdi'
            : 'Web Sitesi Bulunamadı (404)'}
        </h1>

        <p className="text-sm text-slate-400 max-w-md mb-8 leading-relaxed">
          {resolution?.error || 'Aradığınız web sitesi henüz yayınlanmamış veya alan adı yönlendirmesi tamamlanmamış olabilir.'}
        </p>

        {onExitPreview && (
          <button
            onClick={onExitPreview}
            className="px-6 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold transition flex items-center gap-2 border border-slate-700"
          >
            Panele Geri Dön
          </button>
        )}
      </div>
    );
  }

  const snapshot = resolution.snapshot;
  const pages = snapshot.pages || [];
  const currentPage =
    pages.find((p) => p && p.slug === currentPath) ||
    pages.find((p) => p && p.isHomePage) ||
    pages[0];

  const handleOpenReservation = (yacht?: YachtItem) => {
    setTargetYachtForReservation(yacht);
    setIsReservationModalOpen(true);
    setResSubmitted(false);
    setFormError(null);
  };

  const handleReservationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // 1. Anti-bot honeypot check
    if (honeypotValue.trim() !== '') {
      console.warn('Bot detected via honeypot field.');
      setResSubmitted(true);
      return;
    }

    // 2. Client-side Rate Limit check (max 3 submissions per minute per client)
    const allowed = checkSubmissionRateLimit('reservation_form', 3, 60000);
    if (!allowed) {
      setFormError('Çok fazla istek gönderdiniz. Lütfen 1 dakika sonra tekrar deneyiniz.');
      return;
    }

    // 3. Validation
    if (!resName.trim() || !resEmail.trim() || !resPhone.trim()) {
      setFormError('Lütfen ad, e-posta ve telefon alanlarını eksiksiz doldurunuz.');
      return;
    }

    setIsSubmittingRes(true);
    try {
      if (resolution.siteId) {
        await dbService.createReservationInquiry(resolution.siteId, {
          siteId: resolution.siteId,
          customerName: escapeHtml(resName),
          customerEmail: escapeHtml(resEmail),
          customerPhone: escapeHtml(resPhone),
          startDate: resStart,
          endDate: resEnd,
          guestCount: Number(resGuests) || 2,
          yachtId: targetYachtForReservation?.id || 'fleet-general',
          yachtName: targetYachtForReservation?.name || 'Genel Filo Talebi',
          totalEstimatedPrice: targetYachtForReservation ? targetYachtForReservation.pricePerDay * 7 : 0,
          currency: 'EUR',
          notes: escapeHtml(resNotes),
          message: escapeHtml(resNotes),
        });
      }

      setResSubmitted(true);
      try {
        confetti({ particleCount: 60, spread: 60, origin: { y: 0.6 } });
      } catch {
        // optional confetti
      }
    } catch (err: any) {
      setFormError(err.message || 'Rezervasyon iletilirken bir hata oluştu.');
    } finally {
      setIsSubmittingRes(false);
    }
  };

  // Dynamic Independent SEO computation (Rule: Bağımsız SEO Katmanı via react-helmet-async)
  const primaryDomain =
    snapshot.domains?.find((d) => d.isPrimary)?.domain ||
    resolution.normalizedDomain ||
    (typeof window !== 'undefined' ? window.location.hostname : '');
  const canonicalBase = primaryDomain.startsWith('http') ? primaryDomain : `https://${primaryDomain}`;
  const cleanPath = currentPath === '/' ? '' : currentPath;
  const canonicalUrl = `${canonicalBase}${cleanPath}`;

  const metaTitle =
    currentPage?.seoTitle ||
    (currentPage?.title && currentPage?.title.toLowerCase() !== 'ana sayfa'
      ? `${currentPage.title} | ${snapshot.name}`
      : snapshot.seo?.title || (snapshot.settings as any)?.tagline
      ? `${snapshot.name} — ${(snapshot.settings as any)?.tagline}`
      : `${snapshot.name} — Resmi Web Sitesi`);

  const metaDescription =
    currentPage?.seoDescription ||
    snapshot.seo?.description ||
    (snapshot.settings as any)?.description ||
    `${snapshot.name} resmi web sitesi. Profesyonel hizmetler ve güncel bilgiler.`;

  const metaKeywords =
    (currentPage as any)?.keywords?.join?.(', ') ||
    (Array.isArray(snapshot.seo?.keywords) ? snapshot.seo.keywords.join(', ') : snapshot.seo?.keywords) ||
    '';

  const faviconUrl =
    (snapshot.settings as any)?.faviconUrl ||
    (snapshot.theme as any)?.faviconUrl ||
    snapshot.theme?.logoUrl ||
    '';

  const metaImage = snapshot.theme?.logoUrl || (snapshot.settings as any)?.heroImageUrl || '';

  return (
    <WebsiteThemeProvider
      siteSettings={{ ...(snapshot.settings || {}), ...(snapshot.theme || {}) }}
      websiteId={snapshot.siteId}
      className="min-h-screen flex flex-col selection:bg-teal-500 selection:text-white"
    >
      {/* Bağımsız SEO Katmanı: Dynamic <head> injection overriding Adiosoft index.html */}
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        {metaKeywords ? <meta name="keywords" content={metaKeywords} /> : null}
        <link rel="canonical" href={canonicalUrl} />
        {faviconUrl ? <link rel="icon" type="image/x-icon" href={faviconUrl} /> : null}
        {faviconUrl ? <link rel="shortcut icon" href={faviconUrl} /> : null}
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={snapshot.name} />
        {metaImage ? <meta property="og:image" content={metaImage} /> : null}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        {metaImage ? <meta name="twitter:image" content={metaImage} /> : null}
      </Helmet>
      {/* Optional Top Bar for Preview Exit */}
      {onExitPreview && (
        <div className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold flex items-center justify-between z-50 sticky top-0 border-b border-slate-800 shadow-md">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="font-mono">Canlı Site Snapshot Önizlemesi ({snapshot.versionId})</span>
          </div>
          <button
            onClick={onExitPreview}
            className="px-3 py-1 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-200 transition text-[11px]"
          >
            Önizlemeden Çık
          </button>
        </div>
      )}

      {/* Public Header / Navigation: Rendered ONLY if the page does NOT already have a dedicated HEADER or NAVIGATION section */}
      {!Boolean(
        currentPage?.sections?.some(
          (s) =>
            s &&
            (s.type === 'HEADER' ||
              s.type?.toUpperCase() === 'HEADER' ||
              (s as any).category === 'NAVIGATION' ||
              s.name?.toLowerCase().includes('navigasyon') ||
              s.name?.toLowerCase().includes('header') ||
              s.id?.toLowerCase().includes('header')) &&
            s.isVisible !== false
        ) ||
          pages?.some((p) =>
            p.sections?.some(
              (s) =>
                s &&
                (s.type === 'HEADER' || s.type?.toUpperCase() === 'HEADER') &&
                s.isVisible !== false
            )
          )
      ) && (
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-xs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-18 flex items-center justify-between">
            {/* Brand Logo & Name */}
            <div
              onClick={() => {
                setCurrentPath('/');
                setSelectedYacht(null);
                setSelectedBlogPost(null);
              }}
              className="flex items-center gap-3 cursor-pointer select-none"
            >
              {snapshot.theme?.logoUrl ? (
                <img
                  src={snapshot.theme.logoUrl}
                  alt={snapshot.name}
                  className="h-9 w-auto object-contain"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-base shadow"
                  style={{ backgroundColor: snapshot.theme?.primaryColor || '#0284C7' }}
                >
                  {snapshot.name.charAt(0)}
                </div>
              )}
              <span className="font-bold text-lg text-slate-900 tracking-tight">
                {snapshot.theme?.logoText || snapshot.name}
              </span>
            </div>

            {/* Navigation Links */}
            <nav className="hidden md:flex items-center gap-6 text-sm font-semibold">
              {snapshot.navigation?.map((navItem) => (
                <button
                  key={navItem.id}
                  onClick={() => {
                    setCurrentPath(navItem.slug);
                    setSelectedYacht(null);
                    setSelectedBlogPost(null);
                  }}
                  className={`transition-colors py-1 relative ${
                    currentPath === navItem.slug
                      ? 'text-slate-900 font-bold'
                      : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {navItem.title}
                  {currentPath === navItem.slug && (
                    <span
                      className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                      style={{ backgroundColor: snapshot.theme?.primaryColor || '#0284C7' }}
                    />
                  )}
                </button>
              ))}
            </nav>

            {/* CTA Action Button */}
            <div className="flex items-center gap-3">
              {snapshot.industry === 'YACHT_RENTAL' ? (
                <button
                  onClick={() => handleOpenReservation()}
                  className="px-4 py-2.5 rounded-xl text-white text-xs sm:text-sm font-bold shadow-md hover:opacity-90 transition flex items-center gap-2"
                  style={{ backgroundColor: snapshot.theme?.primaryColor || '#0284C7' }}
                >
                  <Anchor className="w-4 h-4" />
                  <span>Tekne Kirala</span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    const contactPage = pages.find((p) => p.slug === '/iletisim');
                    if (contactPage) setCurrentPath('/iletisim');
                  }}
                  className="px-4 py-2.5 rounded-xl text-white text-xs sm:text-sm font-bold shadow-md hover:opacity-90 transition flex items-center gap-2"
                  style={{ backgroundColor: snapshot.theme?.primaryColor || '#0284C7' }}
                >
                  <Phone className="w-4 h-4" />
                  <span>Bize Ulaşın</span>
                </button>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Main Content: Render sections of the active page from snapshot */}
      <main className="flex-1">
        {currentPage?.sections && currentPage.sections.length > 0 ? (
          <div className="space-y-0">
            {currentPage.sections.map((section) => (
              <SectionRenderer
                key={section.id}
                section={section}
                settings={snapshot.settings || ({} as any)}
                industry={snapshot.industry}
                siteData={{
                  id: snapshot.siteId,
                  ownerId: (snapshot as any).ownerId || '',
                  name: snapshot.name,
                  slug: snapshot.subdomain,
                  subdomain: snapshot.subdomain,
                  industry: snapshot.industry,
                  templateId: snapshot.templateId || 'corp-apex',
                  status: 'PUBLISHED',
                  createdAt: snapshot.publishedAt,
                  updatedAt: snapshot.publishedAt,
                  settings: snapshot.settings,
                  pages: snapshot.pages,
                }}
                activePage={currentPage}
                onNavigate={(slug) => {
                  if (slug.startsWith('/#') || slug.startsWith('#')) {
                    const anchor = slug.replace(/^\/?#/, '');
                    const el = document.getElementById(anchor) || document.querySelector(`[data-section-id="${anchor}"]`) || document.querySelector(`#${anchor}`);
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth' });
                    }
                    return;
                  }
                  setCurrentPath(slug);
                  setSelectedYacht(null);
                  setSelectedBlogPost(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onSelectYacht={(yacht) => {
                  setSelectedYacht(yacht);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onOpenReservationModal={(yacht) => handleOpenReservation(yacht)}
                onOpenBlogPost={(post) => {
                  setSelectedBlogPost(post);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            ))}
          </div>
        ) : (
          <div className="py-24 text-center text-slate-400">
            <p className="text-sm">Bu sayfada henüz yayınlanmış bir bölüm bulunmamaktadır.</p>
          </div>
        )}
      </main>

      {/* Mandatory Platform Attribution (Adiosoft Website Builder ile yapılmıştır) */}
      <PlatformAttribution
        bgColor={
          currentPage?.sections?.find((s) => s.type === 'FOOTER' && s.isVisible !== false)?.style?.backgroundColor ||
          currentPage?.sections?.filter((s) => s.isVisible !== false).slice(-1)[0]?.style?.backgroundColor ||
          snapshot.theme?.primaryColor ||
          '#020617'
        }
      />

      {/* Yacht Reservation Modal */}
      {isReservationModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-slate-100 space-y-6 relative animate-in fade-in zoom-in-95">
            <button
              onClick={() => setIsReservationModalOpen(false)}
              className="absolute top-6 right-6 p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
            >
              ✕
            </button>

            <div>
              <div className="flex items-center gap-2 text-teal-600 text-xs font-bold uppercase tracking-wider">
                <Anchor className="w-4 h-4" />
                {targetYachtForReservation?.name || 'Lüks Yat Kiralama Rezervasyonu'}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mt-1">Ön Rezervasyon & Teklif Formu</h3>
              <p className="text-xs text-slate-500 mt-1">
                Talebinizi iletin, kaptanımız ve uzman ekibimiz en geç 2 saat içinde sizinle iletişime geçsin.
              </p>
            </div>

            {resSubmitted ? (
              <div className="p-6 rounded-2xl bg-emerald-50 border border-emerald-200 text-center space-y-3">
                <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
                <h4 className="text-base font-bold text-emerald-900">Talebiniz Başarıyla Alındı!</h4>
                <p className="text-xs text-emerald-700 leading-relaxed">
                  Ön rezervasyon talebiniz sistemimize işlendi. Ekibimiz sizinle en kısa sürede iletişime geçecektir.
                </p>
                <button
                  onClick={() => setIsReservationModalOpen(false)}
                  className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl transition shadow"
                >
                  Tamam
                </button>
              </div>
            ) : (
              <form onSubmit={handleReservationSubmit} className="space-y-4">
                {formError && (
                  <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-xl flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    <span>{formError}</span>
                  </div>
                )}

                {/* Honeypot field for anti-bot protection */}
                <input
                  type="text"
                  name="website_token"
                  value={honeypotValue}
                  onChange={(e) => setHoneypotValue(e.target.value)}
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  autoComplete="off"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Adınız Soyadınız *</label>
                    <input
                      type="text"
                      required
                      value={resName}
                      onChange={(e) => setResName(e.target.value)}
                      placeholder="Ahmet Yılmaz"
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">E-Posta Adresiniz *</label>
                    <input
                      type="email"
                      required
                      value={resEmail}
                      onChange={(e) => setResEmail(e.target.value)}
                      placeholder="ahmet@example.com"
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Telefon *</label>
                    <input
                      type="tel"
                      required
                      value={resPhone}
                      onChange={(e) => setResPhone(e.target.value)}
                      placeholder="+90 532 ..."
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Başlangıç Tarihi</label>
                    <input
                      type="date"
                      value={resStart}
                      onChange={(e) => setResStart(e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Misafir Sayısı</label>
                    <input
                      type="number"
                      min={1}
                      max={30}
                      value={resGuests}
                      onChange={(e) => setResGuests(Number(e.target.value))}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Özel İstekler & Rota Tercihleri</label>
                  <textarea
                    rows={3}
                    value={resNotes}
                    onChange={(e) => setResNotes(e.target.value)}
                    placeholder="Göcek koyları, özel aşçı talebi vb..."
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmittingRes}
                  className="w-full py-3 bg-teal-600 hover:bg-teal-500 text-white text-xs font-bold rounded-xl transition shadow flex items-center justify-center gap-2"
                >
                  {isSubmittingRes ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Talebi Gönder
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* YACHT DETAIL MODAL */}
      {/* ========================================================================= */}
      {selectedYacht && (
        <PublicYachtDetailModal
          yacht={selectedYacht}
          onClose={() => setSelectedYacht(null)}
          onBookYacht={(yacht) => {
            setSelectedYacht(null);
            handleOpenReservation(yacht);
          }}
          primaryColor={snapshot.theme?.primaryColor || '#0284C7'}
        />
      )}

      {/* ========================================================================= */}
      {/* BLOG POST DETAIL MODAL */}
      {/* ========================================================================= */}
      {selectedBlogPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]">
            <div className="relative h-56 bg-slate-100 overflow-hidden shrink-0">
              <img
                src={selectedBlogPost.coverImage}
                alt={selectedBlogPost.title}
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
              <button
                onClick={() => setSelectedBlogPost(null)}
                className="absolute top-4 right-4 p-2 rounded-full bg-slate-900/70 text-white hover:bg-slate-900 transition"
              >
                <X className="w-4 h-4" />
              </button>
              <div
                className="absolute bottom-4 left-4 px-3 py-1 rounded-lg text-white font-bold text-xs"
                style={{ backgroundColor: snapshot.theme?.primaryColor || '#0284C7' }}
              >
                {selectedBlogPost.category}
              </div>
            </div>

            <div className="p-6 overflow-y-auto space-y-4 text-xs text-slate-700">
              <div>
                <h2 className="text-xl font-extrabold text-slate-900">{selectedBlogPost.title}</h2>
                <div className="flex items-center gap-4 text-slate-400 mt-2 text-[11px] pb-3 border-b border-slate-100">
                  <span>Yazar: {selectedBlogPost.authorName}</span>
                  <span>{selectedBlogPost.readTimeMinutes} dk okuma</span>
                </div>
              </div>

              <div className="text-slate-600 leading-relaxed text-sm whitespace-pre-line">
                {selectedBlogPost.content || selectedBlogPost.summary}
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end">
                <button
                  onClick={() => setSelectedBlogPost(null)}
                  className="px-4 py-2 bg-slate-900 text-white rounded-xl font-bold cursor-pointer"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </WebsiteThemeProvider>
  );
});
StandalonePublicWebsiteEngine.displayName = 'StandalonePublicWebsiteEngine';
export default StandalonePublicWebsiteEngine;
